import { useCallback, useEffect, useRef } from 'react';

interface UseInfiniteScrollOptions {
    /**
     * The scrollable container element ref
     */
    containerRef: React.RefObject<HTMLElement>;
    /**
     * Whether there are more items to load
     */
    hasMore: boolean;
    /**
     * Whether we're currently loading more items
     */
    isLoadingMore: boolean;
    /**
     * Function to load more items
     */
    loadMore: () => Promise<void>;
    /**
     * Distance from bottom of container to trigger loading (in pixels)
     * @default 50
     */
    threshold?: number;
    /**
     * The name of the component to log the loading more event
     */
    name?: string;
    resetTrigger?: string; // Can be any value that changes when you want to reset
}

/**
 * A hook that handles infinite scroll logic for a scrollable container
 *
 * @example
 * ```tsx
 * const containerRef = useRef<HTMLDivElement>(null);
 * const { data, hasMore, isLoadingMore, loadMore } = usePaginatedQuery(...);
 *
 * useInfiniteScroll({
 *   containerRef,
 *   hasMore,
 *   isLoadingMore,
 *   loadMore,
 * });
 * ```
 */
export function useInfiniteScroll ({
    containerRef,
    hasMore,
    isLoadingMore,
    loadMore,
    threshold = 50,
    name = '',
    resetTrigger,
}: UseInfiniteScrollOptions): void {
    const timeoutRef = useRef<NodeJS.Timeout>();
    const lastLoadTimeRef = useRef<number>(0);
    const DEBOUNCE_TIME = 500; // Increased debounce time to prevent rapid firing
    useEffect(() => {
        lastLoadTimeRef.current = 0;
    }, [resetTrigger]);

    const handleScroll = useCallback((e: Event) => {
        const target = e.target as HTMLElement;
        const now = Date.now();
        // Don't trigger if we're loading, don't have more items, or if we've triggered a load recently
        if (
            isLoadingMore ||
            !hasMore ||
            (now - lastLoadTimeRef.current < DEBOUNCE_TIME)
        ) {
            return;
        }
        if (
            target.scrollHeight - target.scrollTop - target.offsetHeight < threshold
        ) {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
            timeoutRef.current = setTimeout(() => {
                lastLoadTimeRef.current = now;
                void loadMore();
            }, 100);
        }
    }, [hasMore, isLoadingMore, loadMore, threshold, resetTrigger]);

    useEffect(() => {
        const container = containerRef.current;

        if (!container) {
            return;
        }

        container.addEventListener('scroll', handleScroll);

        return () => {
            container.removeEventListener('scroll', handleScroll);
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, [containerRef, handleScroll]);
}
